import type { Offer, OfferLine, OfferLineTemplate } from "@/hooks/use-offers";
import type { OfferSection } from "@/hooks/use-offer-sections";
import { OfferCatalogDialog } from "@/components/offer/OfferCatalogDialog";
import { OfferConvertConfirmDialog } from "@/components/offer/OfferConvertConfirmDialog";
import { ConvertOfferDialog } from "@/components/offer/ConvertOfferDialog";
import { OfferSettingsDialog } from "@/components/offer/OfferSettingsDialog";
import { AddOfferSectionDialog } from "@/components/offer/AddOfferSectionDialog";
import { OfferKeyboardHelpDialog } from "@/components/offer/OfferKeyboardHelpDialog";
import { OfferLineEditSheet } from "@/components/offer/OfferLineEditSheet";

interface OfferEditDialogsProps {
  offer: Offer;
  lines: OfferLine[];
  sections: OfferSection[];
  templates: OfferLineTemplate[];
  hourlyRate: number;
  /** Katalogens timesats per linje; default `hourlyRate` (VIB-2097). */
  rateForLine?: (line: OfferLine) => number;

  // Catalog
  catalogDialogOpen: boolean;
  onCatalogOpenChange: (open: boolean) => void;
  onPickTemplate: (template: OfferLineTemplate, qty: number | null) => Promise<void> | void;

  // Convert confirm + convert
  convertConfirmOpen: boolean;
  onConvertConfirmOpenChange: (open: boolean) => void;
  onConvertConfirm: () => void;
  convertOpen: boolean;
  onConvertOpenChange: (open: boolean) => void;

  // Settings
  settingsOpen: boolean;
  onSettingsOpenChange: (open: boolean) => void;

  // Add section
  addSectionOpen: boolean;
  onAddSectionOpenChange: (open: boolean) => void;
  addSectionNextSortOrder: number;

  // Keyboard help
  keyboardHelpOpen: boolean;
  onKeyboardHelpOpenChange: (open: boolean) => void;

  // Mobile edit sheet
  editingLine: OfferLine | null;
  editingLineId: string | null;
  onEditingLineOpenChange: (open: boolean) => void;
  onSheetSave: (patch: Partial<OfferLine>) => void;
  onSheetDelete: () => void;
}

/**
 * Samler alle modale dialoger og sheets for OfferEditPage i én komponent.
 * Splittet ut fra page (VIB-658) for å holde page-orkestratoren under
 * 300 linjer. Ingen forretningslogikk her — kun rendring + delegering
 * tilbake til parent.
 */
export function OfferEditDialogs({
  offer,
  lines,
  sections,
  templates,
  hourlyRate,
  rateForLine,
  catalogDialogOpen,
  onCatalogOpenChange,
  onPickTemplate,
  convertConfirmOpen,
  onConvertConfirmOpenChange,
  onConvertConfirm,
  convertOpen,
  onConvertOpenChange,
  settingsOpen,
  onSettingsOpenChange,
  addSectionOpen,
  onAddSectionOpenChange,
  addSectionNextSortOrder,
  keyboardHelpOpen,
  onKeyboardHelpOpenChange,
  editingLine,
  editingLineId,
  onEditingLineOpenChange,
  onSheetSave,
  onSheetDelete,
}: OfferEditDialogsProps) {
  return (
    <>
      <OfferCatalogDialog
        open={catalogDialogOpen}
        onOpenChange={onCatalogOpenChange}
        templates={templates}
        onPick={onPickTemplate}
      />

      <OfferKeyboardHelpDialog
        open={keyboardHelpOpen}
        onOpenChange={onKeyboardHelpOpenChange}
      />

      <OfferConvertConfirmDialog
        open={convertConfirmOpen}
        onOpenChange={onConvertConfirmOpenChange}
        lineCount={lines.length}
        onConfirm={onConvertConfirm}
      />

      <ConvertOfferDialog
        offer={offer}
        lines={lines}
        open={convertOpen}
        onOpenChange={onConvertOpenChange}
      />

      <OfferSettingsDialog
        offer={offer}
        open={settingsOpen}
        onOpenChange={onSettingsOpenChange}
      />

      <AddOfferSectionDialog
        offerId={offer.id}
        open={addSectionOpen}
        onOpenChange={onAddSectionOpenChange}
        nextSortOrder={addSectionNextSortOrder}
      />

      <OfferLineEditSheet
        line={editingLine}
        hourlyRate={editingLine && rateForLine ? rateForLine(editingLine) : hourlyRate}
        open={!!editingLineId}
        onOpenChange={onEditingLineOpenChange}
        onSave={onSheetSave}
        onDelete={onSheetDelete}
      />
    </>
  );
}
